/**
 * `login` — the sign-in screen, which had no catalogue at all until now (PRD-27 F-14).
 *
 * It is the one screen a person reads before they have an account whose language could have
 * been looked up, the one screen `AppShell` does not wrap (F-23), and the first thing a new
 * deployment is judged by. So it is also the screen where an English island would be most
 * visible and least excusable.
 *
 * English is the source of truth AND the type (PRD-27 LOC-011): `Messages = typeof en`, so
 * the Portuguese sibling is checked against the shape of this file and a missing key is a
 * compile error rather than a runtime fallback.
 *
 * What is deliberately NOT here: the provider's own label (`Microsoft`, `Entra ID` — a
 * proper noun, D-17), the standfirst under the title (the brand's tagline, which belongs to
 * the brand profile and gets its per-locale prose in Phase 6, D-12), and the seeded
 * development address in the email field, which is an account id rather than a placeholder.
 */
export const login = {
  title: 'Sign in',

  /** Before the provider listing answers. Two mechanisms, both of which a deployment may lack. */
  checkingMethods: 'Checking available sign-in methods…',
  /**
   * A federated provider's button. The label is the issuer's own name and never translates
   * (D-17); the verb around it does, and in Portuguese it governs a different preposition —
   * which is exactly why this is a function returning the whole phrase rather than a prefix.
   */
  continueWith: (provider: string): string => `Continue with ${provider}`,

  /** Between the federated buttons and the password form, when a deployment offers both. */
  or: 'or',

  emailLabel: 'Email',
  passwordLabel: 'Password',
  submit: 'Sign in',
  signingIn: 'Signing in…',
  /**
   * Shown only when the server sent no sentence of its own. The server's refusal is the
   * better one when there is one, because it distinguishes a wrong password from a
   * deactivated account; this is the fallback for a body that carried neither.
   */
  signInFailed: 'That did not work. Check the password and try again.',
  devNotice: 'Development sign-in. Disabled automatically in production.',

  /**
   * An unreachable API and a deployment with nothing configured both leave the provider list
   * empty, and they are NOT the same dead end: one is temporary, the other needs somebody to
   * edit configuration. Two entries, because collapsing them sends whoever is looking at an
   * outage off to set a variable that is already set.
   */
  unreachable:
    'Sign-in is unavailable at the moment — the service could not be reached. This is usually temporary; try again in a moment. If it keeps happening, an administrator should check that the platform is running.',

  /**
   * One sentence around a `<code>` element, which is why it is two entries rather than one.
   *
   * This is the rich-text exception and nothing more: a catalogue is data, never markup, and
   * nothing here is ever handed to `dangerouslySetInnerHTML`. The view composes the two
   * halves around the variable name, which is an identifier and never translates. Both
   * languages put that identifier mid-sentence, so the split lands in the same place in each.
   */
  noMethod: {
    before: 'No sign-in method is configured. An administrator needs to set',
    after: 'before anyone can sign in.'
  }
}
